import { create } from 'zustand'
import { loadQboListsCache, saveQboListsCache } from '@/utils/qboListsCache'

interface QBOVendor {
  id: string
  display_name: string
  company_name: string | null
  active: boolean
}

interface QBOLocation {
  id: string
  name: string
  active: boolean
}

interface QBOCategory {
  id: string
  name: string
  account_type: string | null
  active: boolean
}

interface QBOState {
  vendors: QBOVendor[]
  locations: QBOLocation[]
  categories: QBOCategory[]
  vendorsSyncedAt: Date | null
  locationsSyncedAt: Date | null
  categoriesSyncedAt: Date | null
  loading: boolean

  setVendors: (vendors: QBOVendor[], syncedAt: Date) => void
  setLocations: (locations: QBOLocation[], syncedAt: Date) => void
  setCategories: (categories: QBOCategory[], syncedAt: Date) => void
  setLoading: (loading: boolean) => void
  hydrateFromCache: () => void
}

const cachedLists = loadQboListsCache()
const cachedAt = cachedLists ? new Date(cachedLists.savedAt) : null

export const useQBOStore = create<QBOState>((set, get) => ({
  vendors: cachedLists?.vendors ?? [],
  locations: cachedLists?.locations ?? [],
  categories: cachedLists?.categories ?? [],
  vendorsSyncedAt: cachedAt,
  locationsSyncedAt: cachedAt,
  categoriesSyncedAt: cachedAt,
  loading: false,

  setVendors: (vendors, syncedAt) => {
    set({ vendors, vendorsSyncedAt: syncedAt })
    const state = get()
    saveQboListsCache({
      vendors,
      locations: state.locations,
      categories: state.categories,
    })
  },

  setLocations: (locations, syncedAt) => {
    set({ locations, locationsSyncedAt: syncedAt })
    const state = get()
    saveQboListsCache({
      vendors: state.vendors,
      locations,
      categories: state.categories,
    })
  },

  setCategories: (categories, syncedAt) => {
    set({ categories, categoriesSyncedAt: syncedAt })
    const state = get()
    saveQboListsCache({
      vendors: state.vendors,
      locations: state.locations,
      categories,
    })
  },

  setLoading: (loading) => set({ loading }),

  hydrateFromCache: () => {
    const cached = loadQboListsCache()
    if (!cached) return
    const syncedAt = new Date(cached.savedAt)
    set({
      vendors: cached.vendors,
      locations: cached.locations,
      categories: cached.categories,
      vendorsSyncedAt: syncedAt,
      locationsSyncedAt: syncedAt,
      categoriesSyncedAt: syncedAt,
    })
  },
}))
